<template>
  <div class="row text-justify-between mb-3 lh-sm">
    <div class="col-12 form-text mb-1" v-html="$t('detailsForm.monitoring.b1')"></div>
  </div>
  <div class="row">
    <!-- First Column: Selects for Data Type and Variable of Interest -->
    <div class="col-12 col-md-6 col-lg-6 col-xl-5">
      <label for="monitoring_variable" class="form-label">
        <span class="me-1">{{ $t('InputSearchFormLabelTitle.Datatype') }}</span>
        <i class="bi bi-info-circle tooltip-init cursor-pointer me-1"
           data-bs-toggle="tooltip"
           data-bs-html="true"
           @click="tooltipGlossaryOnClick"
           :data-bs-title='`<small>${$t(tooltipGlossaryVariableKey())} <span class="fw-semibold">${tooltipGlossaryVariableName()}</span></small>`'></i>
        <div class="spinner-grow spinner-grow-sm" role="status" v-if="componentIsLoading === true">
          <span class="visually-hidden">Loading...</span>
        </div>
      </label>
      <select id="monitoring_variable"
              :disabled="searchIsLoading === true || componentIsLoading === true"
              class="form-select" aria-label="Variable Select"
              v-model="variable">
        <option disabled value="" selected>{{ $t('formDataType.selectDataType') }}</option>
        <template v-for="(item, key) in this.variableMap">
          <option :value="key">{{ item.getNameI18N(this.$i18n.locale) }}</option>
        </template>
      </select>
    </div>

    <!-- Second Column: Separate Inputs for Month and Year -->
    <div class="col-12 col-md-6 col-lg-6 col-xl-5">
      <div class="col-12">
        <label for="datePicker" class="form-label">{{ $t('InputSearchFormLabelTitle.Month') }}</label>
        <VueDatePicker
            ref="datePickerMonth"
            v-model="monthYear"
            month-picker
            year-picker
            auto-apply
            prevent-min-max-navigation
            :teleport="'#dashboardCalendar'"
            format="MMM/yyy"
            :locale="this.$i18n.locale"
            :format-locale="localeToFuncLocale(this.$i18n.locale)"
            :start-date="variableFocusDate"
            :min-date="variableMinDate"
            :max-date="variableMaxDate"
            :year-range="variableYearRange"
            :allowed-dates="variableAvailableDates"
            :disabled-dates="variableMissingDates"
            :disabled="monthIsEnabled === false ||
                        searchIsLoading === true ||
                        componentIsLoading === true ||
                        variableAvailableDates.length === 0"
            :clearable="false"
        />
      </div>
    </div>

    <!-- Third Column: Search Button -->
    <div class="col-12 col-lg-12 col-xl-2 mt-3 mt-xl-0 align-self-end">
      <button type="button" class="btn btn-success w-100" :disabled="isSearchDisabled"
              @click="onSearchClick">
        <i v-if="propBlockComponent === true" class="bi bi-pause-circle pe-2"></i>
        <template v-if="propBlockComponent === false">
          <div v-if="searchIsLoading === true" class="spinner-grow spinner-grow-sm me-2" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <i v-if="searchIsLoading === false" class="bi pe-2"
             :class="{
              'bi-compass': isLastUUIDVisible === false,
              'bi-check-circle-fill': isLastUUIDVisible === true,
            }"></i>
        </template>
        <span class="small">{{ $t('ExploreButtonSearchForm.Explore') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import Storage from "@/libs/Storage";
import constants from "@/libs/Constants";
import '@vuepic/vue-datepicker/dist/main.css';
import {yearMonthToStr} from '@/libs/Helpers';
import ApiConnector from '@/libs/ApiConnection';
import {localeToFuncLocale} from "@/libs/locales";
import VueDatePicker from '@vuepic/vue-datepicker';
import GroupErrorTranslation from '@/libs/locales/errors';
import {VariableColorConfigurationInterface} from "@/libs/Colors";
import {VariableConfiguration, VisualizationMonitoringData} from '@/libs/VisualizationData';
import {
  disposeTooltipsByClass,
  setupTooltipsByClass,
  monthYearCalendarProperties
} from "@/libs/Helpers";

export default {
  components: {
    VueDatePicker
  },
  emits: ['onSearchClick', 'onSearchClickError', 'onSearchClickWait', 'afterSearchClickResult', 'tooltipGlossaryOnClick'],
  props: {
    propInitializeTooltips: Boolean,
    propLastUuid: String,
    propBlockComponent: Boolean
  },
  watch: {
    variable(newValue, oldValue) {
      if (newValue !== oldValue)
        this.monthYear = null;
    }
  },
  data() {
    return {
      localeToFuncLocale,
      variable: '',           // Holds the selected data type
      monthYear: null,

      //Available data
      variables: [],
      variablesDates: {},
      variableMap: {},

      variableTranslations: {}, //Translations for variables

      //Loading
      componentIsLoading: true,  // Loading state for component
      searchIsLoading: false,     // Loading state for search button
    };
  },
  computed: {
    isVariableSet() {
      return this.variableMap[this.variable] !== undefined;
    },
    yearMonthStr() {
      return this.monthYear !== null ? yearMonthToStr(this.monthYear.year, this.monthYear.month + 1) : null;
    },
    isMonthYearSet() {
      if (this.yearMonthStr === null)
        return false;
      return this.variableAvailableDatesStr.indexOf(this.yearMonthStr) !== -1;
    },
    isFormComplete() {
      return this.isVariableSet !== false && this.isMonthYearSet !== false;
    },
    isLastUUIDVisible() {
      let dataObject = this.createDataObject({});
      if (this.propLastUuid === null || dataObject === null)
        return false;

      return dataObject.uuid === this.propLastUuid
    },
    isSearchDisabled() {
      return this.isFormComplete === false ||
          this.searchIsLoading === true ||
          this.isLastUUIDVisible === true ||
          this.propBlockComponent === true
    },
    variableAvailableDates() {
      let variableData = this.variablesDates[this.variable];
      return variableData !== undefined ? variableData.availableDates : [];
    },
    variableAvailableDatesStr() {
      let variableData = this.variablesDates[this.variable];
      return variableData !== undefined ? variableData.availableDatesStr : [];
    },
    variableMissingDates() {
      let variableData = this.variablesDates[this.variable];
      return variableData !== undefined ? variableData.missingDates : [];
    },
    variableMinDate() {
      let variableData = this.variablesDates[this.variable];
      return variableData !== undefined ? variableData.minDate : null;
    },
    variableMaxDate() {
      let variableData = this.variablesDates[this.variable];
      return variableData !== undefined ? variableData.maxDate : null;
    },
    variableYearRange() {
      let variableData = this.variablesDates[this.variable];
      return variableData !== undefined ? variableData.availableYears : null;
    },
    variableFocusDate() {
      let variableData = this.variablesDates[this.variable];
      return variableData !== undefined ? variableData.focusStartDate : null;
    },
    monthIsEnabled() {
      return this.isVariableSet === true;
    },
  },
  beforeMount() {
    let monitoringLayersKey = 'MonitoringLayers';
    let monitoringLayers = Storage.get(monitoringLayersKey);

    if (monitoringLayers !== null) {
      this.processLayers(monitoringLayers);
      this.componentIsLoading = false;
    } else
      ApiConnector.getMonitoringLayers().then(result => {
        result.json().then(output => {
          //Store item locally (if variable is set)
          let secondsStorage = parseInt(process.env.VUE_APP_KEEP_LAYER_DATA_IN_CACHE_SECONDS);
          if (isNaN(secondsStorage) === false && secondsStorage > 0)
            Storage.set(monitoringLayersKey, output, secondsStorage);

          // Process output from API
          this.processLayers(output);
        });
      }).catch((error) => {
        // Translate message
        this.$emit('onSearchClickError', GroupErrorTranslation.t(error.message, this.$i18n));
      }).finally(() => {
        this.componentIsLoading = false;
      });
  },
  mounted() {
    if (this.propInitializeTooltips === true)
      this.tooltipsSetup();
  },
  beforeUnmount() {
    if (this.propInitializeTooltips === true)
      this.tooltipsDispose();
  },
  methods: {
    processLayers(output) {
      let variables = output.variables;
      let monthProperties = monthYearCalendarProperties(output.months);

      variables.forEach(item => {
        let itemId = item.id;

        let itemColorScaleData = item.color_scale;
        let colorInterface = new VariableColorConfigurationInterface();
        let variableColorPalette = colorInterface.newObject(
            itemColorScaleData.type,
            itemColorScaleData.values
        );

        this.variableMap[itemId] = new VariableConfiguration(
            itemId,
            {
              en: item.name.en,
              it: item.name.it,
            },
            variableColorPalette,
            item.measure_unit_abbreviation,
            item.decimals || 0,
            item.allows_multiple_areas,
            null,
            itemColorScaleData.background_colors
        )

        /**
         * Build calendar properties based on list of months
         * In a previous version of the API, the list of months belonged
         * to each variable. The new output comes with the available months
         * for all variables.
         *
         * Instead of changing the previous behaviour, we decided to keep
         * the list of months per each variable, but setting the same list.
         */
        this.variablesDates[itemId] = monthProperties
      });
    },
    createDataObject(geoJson) {
      if (this.isVariableSet === false || this.isMonthYearSet === false)
        return null;

      // Create the search data object from the form fields
      return new VisualizationMonitoringData(
          this.variableMap[this.variable], //Variable configuration
          this.monthYear.month + 1, // Add 1 to get the correct month (1-12)
          this.monthYear.year,
          this.variablesDates[this.variable],
          geoJson
      );
    },
    async onSearchClick() {
      if (this.isSearchDisabled) {
        // Translate message
        this.$emit('onSearchClickError', GroupErrorTranslation.t(constants.STATUS.PARAMS_MISSING, this.$i18n));
        return;
      }

      // Clear previous data and error messages
      this.searchIsLoading = true;  // Start loading
      this.$emit('onSearchClickWait');

      // Fetch GeoJSON data from the backend API using the service
      ApiConnector.postMonitoringQuery(this.variable, this.yearMonthStr).then(result => {
        result.json().then(output => {
          // Create the search data object from the form fields
          const formData = this.createDataObject(output);

          // Emit the data to the parent component
          this.$emit('onSearchClick', formData);

          /*Timeout to wait some seconds before the filter window is hidden*/
          setTimeout(() => {
            this.searchIsLoading = false;
            this.$emit('afterSearchClickResult');
          }, 700);  // Stop loading
        });
      }).catch((error) => {
        // Handle errors and display the message
        this.$emit('onSearchClickError', GroupErrorTranslation.t(error.message, this.$i18n));
        this.$emit('afterSearchClickResult');
        this.searchIsLoading = false;
      });
    },
    tooltipsDispose() {
      disposeTooltipsByClass('.tooltip-init');
    },
    tooltipsSetup() {
      this.tooltipsDispose();
      setupTooltipsByClass('.tooltip-init');
    },
    tooltipGlossaryVariableKey() {
      return (this.isVariableSet) ? 'tooltips.GlossaryShowVariable' : 'tooltips.GlossaryShowVariablesAvailable';
    },
    tooltipGlossaryVariableName() {
      return this.isVariableSet ? this.variableMap[this.variable].getNameI18N(this.$i18n.locale) : '';
    },
    tooltipGlossaryOnClick() {
      this.$emit('tooltipGlossaryOnClick', this.variable);
    }
  },
  updated() {
    this.$refs.datePickerMonth.parseModel();
    this.tooltipsSetup();
  }
};
</script>